import { TextField } from "@mui/material"
import type { SxProps, Theme } from "@mui/material/styles"

interface DatePickerProps {
  value?: string // yyyy-MM-dd format
  onChange?: (date: string) => void
  placeholder?: string
  className?: string
  disabled?: boolean
  sx?: SxProps<Theme>
}

/**
 * Date picker — a native MUI `TextField type="date"`. Rebuilt off the old
 * Radix `Popover` + react-day-picker `Calendar` (which depended on the
 * Tailwind/shadcn stack) as part of the MUI 5 migration. A native date
 * input's value is already in `yyyy-MM-dd`, so the `{ value, onChange }`
 * contract is identical and every CRM date field works without caller
 * changes (the browser supplies the calendar UI).
 *
 * Sizing: `size="medium"` + `fullWidth` so it reads as a proper, comfortable
 * field rather than a cramped/"miniatured" one (it used to be `size="small"`,
 * which looked tiny next to the rest of a form), and the native calendar
 * indicator is enlarged into a real ~44px-friendly tap target. On touch
 * devices the native input opens the OS date picker — already the best
 * mobile experience.
 */
export function DatePicker({
  value,
  onChange,
  placeholder,
  className,
  disabled = false,
  sx,
}: DatePickerProps) {
  return (
    <TextField
      type="date"
      size="medium"
      fullWidth
      disabled={disabled}
      value={value ?? ""}
      onChange={(e) => onChange?.(e.target.value)}
      InputLabelProps={{ shrink: true }}
      {...(placeholder ? { label: placeholder } : {})}
      {...(className ? { className } : {})}
      sx={[
        {
          // Make the native calendar button a comfortable, obvious tap
          // target (the default browser glyph is tiny — part of why the
          // field read as "miniatured").
          "& input::-webkit-calendar-picker-indicator": {
            cursor: "pointer",
            fontSize: "1.15rem",
            padding: 0.5,
            marginRight: -0.5,
            opacity: 0.65,
            transition: "opacity 150ms",
          },
          "& input:hover::-webkit-calendar-picker-indicator": { opacity: 1 },
          // Keep the typed date comfortably legible.
          "& input[type=date]": { fontSize: "0.95rem" },
        },
        // Merge a caller-supplied sx (which may itself be an array) without
        // nesting — MUI's documented sx-forwarding pattern.
        ...(sx ? (Array.isArray(sx) ? sx : [sx]) : []),
      ]}
    />
  )
}
